import { IconButton } from '@components/iconButton/IconButton';
import { Icon } from '@shared/components/icon/Icon';
import { ModalContainer } from '@shared/components/modal/ModalContainer';
import cx from 'classnames';
import {
  type SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styles from './Carousel.scss';

export type CarouselVariant = 'compact' | 'fullWidth';

/**
 * `fill` — `position: absolute; inset: 0` (parent must have size), e.g. event card header.
 * `intrinsic` — carousel sets min/max height itself for standalone use (e.g. gallery).
 */
export type CarouselCompactSizing = 'fill' | 'intrinsic';

export type CarouselSlide = {
  key: string;
  /** CSS `background-image` value (compact variant). */
  backgroundImage: string;
  /** Optional image URL for fullWidth (`<img src>`); otherwise the last `url(...)` in `backgroundImage` is used. */
  src?: string;
};

const DEFAULT_AUTO_ADVANCE_MS = 5000;
const DEFAULT_TRANSITION_MS = 350;

const URL_IN_BACKGROUND_RE = /url\s*\(\s*["']?([^"')]+)["']?\s*\)/gi;

function imageUrlForSlide(slide: CarouselSlide): string | undefined {
  if (slide.src) {
    return slide.src;
  }
  const matches = [...slide.backgroundImage.matchAll(URL_IN_BACKGROUND_RE)];
  const last = matches[matches.length - 1];
  return last?.[1]?.trim();
}

export type CarouselProps = {
  slides: CarouselSlide[];
  /** Default `fullWidth`. Use `compact` for cover-style slides in a fixed-height slot (e.g. event card header). */
  variant?: CarouselVariant;
  /**
   * Only when `variant` is `compact`. Default `fill` (absolute fill). Use `intrinsic` when the
   * parent has no height (standalone gallery); ignored for `fullWidth`.
   */
  compactSizing?: CarouselCompactSizing;
  /** When true and there are multiple slides, advances automatically (skipped when prefers-reduced-motion). Default true. */
  autoAdvance?: boolean;
  autoAdvanceIntervalMs?: number;
  transitionDurationMs?: number;
  onNavInteraction?: (e: SyntheticEvent) => void;
  className?: string;
  previousImageLabel?: string;
  nextImageLabel?: string;
  /** `alt` for fullWidth `<img>` slides. */
  fullWidthImageAlt?: string;
  /** When false, prev/next buttons are hidden (multi-slide carousels can still auto-advance). Default true. */
  showArrowNavigation?: boolean;
  /** When false, bottom dot indicators are hidden (`fullWidth` only). Default true. */
  showDotNavigation?: boolean;
  /**
   * When both are set, the active slide index is controlled by the parent (e.g. external thumbnails).
   */
  activeIndex?: number;
  onActiveIndexChange?: (index: number) => void;
  /** Merged onto the fullWidth image backdrop container. */
  backdropClassName?: string;
  /** Merged onto the fullWidth `<img>` (all `fullWidthHeightMode` values). */
  fullWidthSlideImageClassName?: string;
  /**
   * `stage` (default) — fixed min/max stage height (hero-style).
   * `fitContent` — height follows the active image at full width (gallery / fluid parent).
   * Slide max-height defaults to 390px; set `--carousel-fit-content-slide-max-height` on an ancestor to override.
   * `fillContainer` — fill a bounded parent (flex/grid); image uses `object-fit: contain` inside the box.
   * `cover` — always fills 100% width and a fixed height, cropping the image (`object-fit: cover`)
   * instead of letterboxing. Height defaults to 450px; set `--carousel-cover-height` on an ancestor
   * to override.
   */
  fullWidthHeightMode?: 'stage' | 'fitContent' | 'fillContainer' | 'cover';
  /**
   * When true, shows a control that opens a modal with a full-width carousel of the same slides
   * (requires each slide to resolve to an image URL via `src` or the last `url(...)` in `backgroundImage`).
   */
  expandToModal?: boolean;
  expandModalTriggerLabel?: string;
  expandModalCloseLabel?: string;
  expandModalDialogLabel?: string;
  /** `alt` for images inside the expand modal; falls back to `fullWidthImageAlt`. */
  expandModalImageAlt?: string;
};

export function Carousel({
  slides,
  variant = 'fullWidth',
  compactSizing = 'fill',
  autoAdvance = true,
  autoAdvanceIntervalMs = DEFAULT_AUTO_ADVANCE_MS,
  transitionDurationMs = DEFAULT_TRANSITION_MS,
  onNavInteraction,
  className,
  previousImageLabel = 'Previous image',
  nextImageLabel = 'Next image',
  fullWidthImageAlt = '',
  showArrowNavigation = true,
  showDotNavigation = true,
  activeIndex: activeIndexProp,
  onActiveIndexChange,
  backdropClassName,
  fullWidthSlideImageClassName,
  fullWidthHeightMode = 'stage',
  expandToModal = false,
  expandModalTriggerLabel = 'View full size',
  expandModalCloseLabel = 'Close gallery',
  expandModalDialogLabel = 'Full size images',
  expandModalImageAlt,
}: CarouselProps) {
  const isControlled =
    typeof activeIndexProp === 'number' && onActiveIndexChange != null;

  const [internalActiveIndex, setInternalActiveIndex] = useState(0);
  const [isExpandModalOpen, setIsExpandModalOpen] = useState(false);
  const [expandModalIndex, setExpandModalIndex] = useState(0);
  const count = slides.length;

  const slidesForExpandModal = useMemo(
    () =>
      slides.map((slide) => ({
        ...slide,
        src: slide.src ?? imageUrlForSlide(slide),
      })),
    [slides],
  );

  const canExpandToModal =
    expandToModal &&
    count > 0 &&
    slidesForExpandModal.some((slide) => Boolean(slide.src));

  const activeIndex = isControlled
    ? Math.min(Math.max(0, activeIndexProp as number), Math.max(0, count - 1))
    : internalActiveIndex;

  const activeIndexRef = useRef(activeIndex);
  activeIndexRef.current = activeIndex;

  const commitIndex = useCallback(
    (nextRaw: number) => {
      if (count === 0) return;
      const next = ((nextRaw % count) + count) % count;
      if (isControlled && onActiveIndexChange) {
        onActiveIndexChange(next);
      } else {
        setInternalActiveIndex(next);
      }
    },
    [count, isControlled, onActiveIndexChange],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset to first slide when slide count changes (uncontrolled only)
  useEffect(() => {
    if (isControlled) return;
    setInternalActiveIndex(0);
  }, [count, isControlled]);

  useEffect(() => {
    if (expandModalIndex < count) return;
    setExpandModalIndex(Math.max(0, count - 1));
  }, [count, expandModalIndex]);

  useEffect(() => {
    if (!autoAdvance || count <= 1) return;

    if (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return;
    }

    const id = window.setInterval(() => {
      commitIndex(activeIndexRef.current + 1);
    }, autoAdvanceIntervalMs);

    return () => window.clearInterval(id);
  }, [autoAdvance, autoAdvanceIntervalMs, count, commitIndex]);

  const handleOpenExpandModal = useCallback(() => {
    setExpandModalIndex(activeIndex);
    setIsExpandModalOpen(true);
  }, [activeIndex]);

  const handleCloseExpandModal = useCallback(() => {
    setIsExpandModalOpen(false);
  }, []);

  if (count === 0) {
    return null;
  }

  const handleNav = onNavInteraction ?? (() => {});

  const isCompact = variant === 'compact';
  const fullWidthFitContent =
    !isCompact && fullWidthHeightMode === 'fitContent';
  const fullWidthFillContainer =
    !isCompact && fullWidthHeightMode === 'fillContainer';
  const fullWidthCover = !isCompact && fullWidthHeightMode === 'cover';

  const activeSlide = slides[activeIndex];
  const activeFullWidthSrc = !isCompact
    ? imageUrlForSlide(activeSlide)
    : undefined;

  const showArrowButtons = count > 1 && showArrowNavigation;
  const showDotIndicators = count > 1 && showDotNavigation && !isCompact;

  const fullWidthSlideImageClass = fullWidthFillContainer
    ? styles.slideImageBounded
    : fullWidthFitContent
      ? styles.slideImageFitContent
      : fullWidthCover
        ? styles.slideImageCover
        : styles.slideImage;

  const arrowNav = showArrowButtons ? (
    <>
      <button
        type="button"
        className={cx(styles.navButton, styles.navPrev)}
        aria-label={previousImageLabel}
        onMouseDown={handleNav}
        onClick={(e) => {
          handleNav(e);
          commitIndex(activeIndex - 1);
        }}
      >
        <Icon name="chevronLeft" />
      </button>
      <button
        type="button"
        className={cx(styles.navButton, styles.navNext)}
        aria-label={nextImageLabel}
        onMouseDown={handleNav}
        onClick={(e) => {
          handleNav(e);
          commitIndex(activeIndex + 1);
        }}
      >
        <Icon name="chevronRight" />
      </button>
    </>
  ) : null;

  const compactRootClass =
    isCompact &&
    (compactSizing === 'intrinsic'
      ? styles.rootCompactIntrinsic
      : styles.rootCompactFill);

  const rootFullWidthClass = fullWidthFillContainer
    ? styles.rootFullWidthFillContainer
    : fullWidthFitContent
      ? styles.rootFullWidthFitContent
      : fullWidthCover
        ? styles.rootFullWidthCover
        : styles.rootFullWidth;

  const expandModalAlt = expandModalImageAlt ?? fullWidthImageAlt;

  return (
    <>
      <div className={cx(compactRootClass || rootFullWidthClass, className)}>
        {isCompact ? (
          <>
            <div
              className={cx(
                styles.backdropCompact,
                compactSizing === 'intrinsic' &&
                  styles.backdropCompactIntrinsic,
              )}
              aria-hidden
            >
              {slides.map((slide, i) => (
                <div
                  key={slide.key}
                  className={cx(styles.slideCompact, {
                    [styles.slideActive]: i === activeIndex,
                  })}
                  data-active={i === activeIndex ? 'true' : 'false'}
                  style={{
                    backgroundImage: slide.backgroundImage,
                    backgroundSize: 'cover',
                    transition: `opacity ${transitionDurationMs}ms ease`,
                  }}
                />
              ))}
            </div>
            {arrowNav}
          </>
        ) : (
          <div
            className={cx(
              styles.fullWidthStage,
              fullWidthFitContent && styles.fullWidthStageFitContent,
              fullWidthFillContainer && styles.fullWidthStageFillContainer,
              fullWidthFillContainer &&
                showDotIndicators &&
                styles.fullWidthStageFillContainerWithDots,
              fullWidthCover && styles.fullWidthStageCover,
            )}
          >
            <div
              className={cx(
                fullWidthFillContainer
                  ? styles.backdropFullWidthFillContainer
                  : fullWidthFitContent
                    ? styles.backdropFullWidthFitContent
                    : fullWidthCover
                      ? styles.backdropFullWidthCover
                      : styles.backdropFullWidth,
                backdropClassName,
              )}
            >
              {activeFullWidthSrc ? (
                <img
                  className={cx(
                    fullWidthSlideImageClass,
                    fullWidthSlideImageClassName,
                  )}
                  src={activeFullWidthSrc}
                  alt={fullWidthImageAlt}
                />
              ) : null}
            </div>
            {showDotIndicators ? (
              <nav className={styles.dotsTrack} aria-label={`${count} photos`}>
                {slides.map((slide, i) => (
                  <button
                    key={slide.key}
                    type="button"
                    className={styles.dotHit}
                    aria-label={`Show image ${i + 1} of ${count}`}
                    aria-current={i === activeIndex ? 'true' : undefined}
                    onMouseDown={handleNav}
                    onClick={(e) => {
                      handleNav(e);
                      commitIndex(i);
                    }}
                  >
                    <span
                      className={cx(styles.dot, {
                        [styles.dotActive]: i === activeIndex,
                      })}
                      aria-hidden
                    />
                  </button>
                ))}
              </nav>
            ) : null}
            {arrowNav}
          </div>
        )}
        {canExpandToModal ? (
          <button
            aria-label={expandModalTriggerLabel}
            className={styles.expandModalTrigger}
            data-testid="carousel-expand-to-modal"
            onClick={handleOpenExpandModal}
            type="button"
          >
            <Icon className={styles.expandModalIcon} name="expand" />
          </button>
        ) : null}
      </div>
      {canExpandToModal ? (
        <ModalContainer
          className={styles.expandGalleryModalRoot}
          isOpen={isExpandModalOpen}
          onClose={handleCloseExpandModal}
        >
          <div
            aria-label={expandModalDialogLabel}
            aria-modal="true"
            className={styles.expandModalSurface}
            onClick={(event) => event.stopPropagation()}
            role="dialog"
          >
            <IconButton
              ariaLabel={expandModalCloseLabel}
              className={styles.expandModalClose}
              iconName="close"
              onClick={handleCloseExpandModal}
            />
            <div className={styles.expandModalCarouselHost}>
              <Carousel
                activeIndex={expandModalIndex}
                autoAdvance={false}
                fullWidthHeightMode="fitContent"
                fullWidthImageAlt={expandModalAlt}
                onActiveIndexChange={setExpandModalIndex}
                slides={slidesForExpandModal}
                variant="fullWidth"
              />
            </div>
          </div>
        </ModalContainer>
      ) : null}
    </>
  );
}
